import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Loader2, ArrowRight } from "lucide-react";
import { MaskLine, Reveal } from "../components/Motion";
import { donate } from "../lib/api";
import { toast } from "sonner";

const TIERS = [
  { amt: 5, note: "A spool of thread" },
  { amt: 10, note: "Fuels the embroidery" },
  { amt: 20, note: "Backs a new sample" },
  { amt: 50, note: "Helps print a run" },
  { amt: 100, note: "Powers the next drop" },
];

const STATS = [
  { n: "100%", l: "Goes straight to the brand" },
  { n: "1 of 1", l: "Independent & self-funded" },
  { n: "∞", l: "Gratitude, right back" },
];

export default function Donate() {
  const [amount, setAmount] = useState(20);
  const [loading, setLoading] = useState(false);

  const give = async () => {
    setLoading(true);
    try {
      const r = await donate(amount, window.location.origin);
      window.location.href = r.checkout_url;
    } catch {
      toast.error("Couldn't start the donation. Try again.");
      setLoading(false);
    }
  };

  const selected = TIERS.find((t) => t.amt === amount);

  return (
    <div data-testid="donate-page">
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-6rem)]">
        {/* LEFT — copy + tiers */}
        <div className="px-4 sm:px-10 lg:px-16 py-16 lg:py-24 flex flex-col justify-center">
          <div className="flex items-center gap-3">
            <Heart size={16} className="text-cs-green" />
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cs-green">Back the brand · Give directly</p>
          </div>
          <h1 className="font-display font-extrabold text-6xl sm:text-7xl lg:text-8xl uppercase leading-[0.82] mt-6">
            <MaskLine>Support</MaskLine>
            <MaskLine delay={0.08} className="text-graytx">the</MaskLine>
            <MaskLine delay={0.16}>Brand</MaskLine>
          </h1>
          <p className="text-graytx mt-7 max-w-md leading-relaxed">
            Love what we're building? Back Covertswag directly. Every dollar helps us produce the next drop, keep the quality high, and grow an independent brand from the ground up.
          </p>

          <div className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graytx mb-4">Choose an amount</p>
            <div className="grid grid-cols-5 gap-2 sm:gap-3" data-testid="donation-tiers">
              {TIERS.map((t) => (
                <button key={t.amt} onClick={() => setAmount(t.amt)} data-testid={`donate-tier-${t.amt}`}
                  className={`h-16 sm:h-20 flex flex-col items-center justify-center border font-display font-bold transition-colors duration-200 ${amount === t.amt ? "bg-cs-green text-cream border-cs-green" : "border-line hover:border-cream text-cream"}`}>
                  <span className="text-lg sm:text-2xl">${t.amt}</span>
                </button>
              ))}
            </div>
            <motion.p key={amount} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cs-yellow" data-testid="donation-note">
              ${amount} ≈ {selected?.note}
            </motion.p>
          </div>

          <button onClick={give} disabled={loading} data-testid="donate-btn"
            className="mt-8 inline-flex items-center justify-center gap-2 bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] px-10 py-5 hover:bg-cs-green hover:text-cream disabled:opacity-60 transition-colors duration-200">
            {loading ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : <>Donate ${amount} <ArrowRight size={15} /></>}
          </button>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-graytx">Secure one-time donation via Stripe</p>
        </div>

        {/* RIGHT — image */}
        <div className="relative min-h-[50vh] lg:min-h-full overflow-hidden order-first lg:order-last">
          <motion.img
            src="/brand/stairwell.png" alt="Fight the cold"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-bg/80 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8">
            <p className="font-editorial italic text-2xl sm:text-3xl leading-tight">
              "Built by us, backed by you."
            </p>
          </div>
        </div>
      </div>

      {/* IMPACT STATS */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-20 grid grid-cols-3 gap-4 sm:gap-8">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.1} className="border-t border-line pt-6">
            <p className="font-display font-extrabold text-4xl sm:text-6xl text-cs-green">{s.n}</p>
            <p className="text-graytx text-sm mt-2">{s.l}</p>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
